import React, { useState } from 'react';
import { Paper, TextField, Grid, Box, Button, Alert, Typography, Tooltip } from '@mui/material';
import {
  Notifications as BellIcon,
  CardGiftcard as GiftIcon,
  AutoAwesome as SparklesIcon,
  Mail as MailIcon,
  Star as StarIcon,
  Favorite as HeartIcon,
  Label as TagIcon,
  Bolt as ZapIcon,
  Security as ShieldIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const BENEFIT_ICON_OPTIONS = [
  { value: 'bell', Icon: BellIcon, labelKey: 'newsletter_benefit_icon_bell' },
  { value: 'gift', Icon: GiftIcon, labelKey: 'newsletter_benefit_icon_gift' },
  { value: 'sparkles', Icon: SparklesIcon, labelKey: 'newsletter_benefit_icon_sparkles' },
  { value: 'mail', Icon: MailIcon, labelKey: 'newsletter_benefit_icon_mail' },
  { value: 'star', Icon: StarIcon, labelKey: 'newsletter_benefit_icon_star' },
  { value: 'heart', Icon: HeartIcon, labelKey: 'newsletter_benefit_icon_heart' },
  { value: 'tag', Icon: TagIcon, labelKey: 'newsletter_benefit_icon_tag' },
  { value: 'zap', Icon: ZapIcon, labelKey: 'newsletter_benefit_icon_zap' },
  { value: 'shield', Icon: ShieldIcon, labelKey: 'newsletter_benefit_icon_shield' },
];

const DEFAULT_BENEFITS = [
  { icon: 'bell', text: 'Entérate primero de nuevos lanzamientos' },
  { icon: 'gift', text: 'Ofertas exclusivas para suscriptores' },
  { icon: 'sparkles', text: 'Contenido especial y novedades' }
];

const NewsletterTab = ({ leadingContent }) => {
  const { t } = useTranslation();
  const { config, handleChange, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);

  const newsletterFields = [
    { path: 'siteConfig.newsletter.title', label: t('newsletter_title') || 'Título' },
    { path: 'siteConfig.newsletter.subtitle', label: t('newsletter_subtitle') || 'Subtítulo' },
    { path: 'siteConfig.newsletter.placeholder', label: t('newsletter_placeholder') || 'Placeholder' },
    { path: 'siteConfig.newsletter.submitText', label: t('newsletter_submit_text') || 'Texto del Botón' },
    { path: 'siteConfig.newsletter.successMessage', label: t('newsletter_success_message') || 'Mensaje de Éxito' }
  ];

  const fields = newsletterFields.filter(field => isFieldConfigurable(field.path));
  const benefitsPath = 'siteConfig.newsletter.benefits';
  const isBenefitsConfigurable = isFieldConfigurable(benefitsPath);
  const rawBenefits = config?.siteConfig?.newsletter?.benefits;
  const benefits = Array.isArray(rawBenefits) && rawBenefits.length >= 3
    ? rawBenefits.slice(0, 3).map(b => ({ icon: b?.icon || 'bell', text: b?.text ?? '' }))
    : [...DEFAULT_BENEFITS];

  const updateBenefit = (index, field, value) => {
    const next = benefits.map((b, i) => i === index ? { ...b, [field]: value } : b);
    handleChange(benefitsPath, next);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updates = {};
      fields.forEach(field => {
        const value = getFieldValue(field.path);
        updates[field.path] = value;
      });
      if (isBenefitsConfigurable) {
        updates[benefitsPath] = benefits;
      }

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving newsletter:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  const getFieldValue = (path) => {
    const value = path.split('.').reduce((obj, key) => obj?.[key], config);
    // Retornar el valor o string vacío si es null/undefined
    return value !== null && value !== undefined ? value : '';
  };

  const hasEditableFields = fields.length > 0 || isBenefitsConfigurable;

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      {leadingContent}
      {!hasEditableFields && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      )}
      <Grid container spacing={2}>
        {fields.map((field) => {
          const isConfigurable = isFieldConfigurable(field.path);
          const isFixed = isFieldFixed(field.path);
          const value = getFieldValue(field.path);

          if (!isConfigurable && !isFixed) return null;

          return (
            <Grid item xs={12} md={field.path.includes('placeholder') || field.path.includes('submitText') || field.path.includes('successMessage') ? 4 : 6} key={field.path}>
              <TextField
                fullWidth
                label={field.label}
                value={value}
                onChange={(e) => handleChange(field.path, e.target.value)}
                disabled={!isConfigurable || isFixed}
                helperText={isFixed ? (t('field_fixed') || 'Este campo es fijo y no se puede modificar') : ''}
              />
            </Grid>
          );
        })}
        {isBenefitsConfigurable && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
              {t('newsletter_benefits_cards') || 'Cards de beneficios (3)'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('newsletter_benefits_cards_helper') || 'Icono y texto de cada card que se muestra en la página de suscripción.'}
            </Typography>
            {[0, 1, 2].map((index) => {
              const currentIcon = benefits[index]?.icon || 'bell';
              const isValidIcon = BENEFIT_ICON_OPTIONS.some((opt) => opt.value === currentIcon);
              const displayIcon = isValidIcon ? currentIcon : 'bell';
              return (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t('newsletter_benefit_icon') || 'Icono'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {BENEFIT_ICON_OPTIONS.map((opt) => {
                          const selected = displayIcon === opt.value;
                          const IconComponent = opt.Icon;
                          return (
                            <Tooltip key={opt.value} title={t(opt.labelKey) || opt.value}>
                              <Box
                                component="button"
                                type="button"
                                onClick={() => updateBenefit(index, 'icon', opt.value)}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  p: 0,
                                  minWidth: 0,
                                  border: '1px solid',
                                  borderColor: selected ? 'primary.main' : 'divider',
                                  borderRadius: 1,
                                  bgcolor: selected ? 'primary.main' : 'transparent',
                                  color: selected ? 'primary.contrastText' : 'text.secondary',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  '&:hover': {
                                    bgcolor: selected ? 'primary.dark' : 'action.hover',
                                    color: selected ? 'primary.contrastText' : 'text.primary',
                                  },
                                }}
                              >
                                <IconComponent sx={{ fontSize: 22 }} />
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={8} md={9}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('newsletter_benefit_text') || 'Texto'}
                        value={benefits[index]?.text ?? ''}
                        onChange={(e) => updateBenefit(index, 'text', e.target.value)}
                        placeholder={index === 0 ? 'Entérate primero de nuevos lanzamientos' : ''}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Grid>
        )}
        {hasEditableFields && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default NewsletterTab;
